import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BankAccount {
  id: string;
  bank_name: string;
  account_number: string;
  agency: string;
  holder_name: string;
  pix_key?: string;
  pix_key_type?: string;
  is_active: boolean;
}

interface Withdrawal {
  id: string;
  amount_cents: number;
  status: 'pending' | 'approved' | 'rejected';
  requested_at: string;
  processed_at?: string | null;
  transaction_id?: string | null;
  bank_account: BankAccount;
  transfer_type: 'pix' | 'bank_transfer';
  admin_notes?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
}

interface FinanceSummary {
  totalEarned: number;
  availableForWithdrawal: number;
  pendingWithdrawals: number;
  pendingReleases: number;
  totalWithdrawn: number;
  digitalContentEarnings?: number;
  digitalContentPendingRelease?: number;
  digitalContentAvailable?: number;
}

export const useMentorFinance = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [financeSummary, setFinanceSummary] = useState<FinanceSummary>({
    totalEarned: 0,
    availableForWithdrawal: 0,
    pendingWithdrawals: 0,
    pendingReleases: 0,
    totalWithdrawn: 0,
    digitalContentEarnings: 0,
    digitalContentPendingRelease: 0,
    digitalContentAvailable: 0,
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (user) {
      fetchFinanceData();
    }
  }, [user]);

  const fetchFinanceData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get mentor ID and commission rate
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor_info')
        .select('id, commission_rate')
        .eq('profile_id', user.id)
        .single();
      
      if (mentorError || !mentorData) {
        console.log('⚠️ [useMentorFinance] Mentor info not found');
        // Set empty state for non-mentors
        setFinanceSummary({
          totalEarned: 0,
          availableForWithdrawal: 0,
          pendingWithdrawals: 0,
          pendingReleases: 0,
          totalWithdrawn: 0,
          digitalContentEarnings: 0,
          digitalContentPendingRelease: 0,
          digitalContentAvailable: 0,
        });
        setBankAccounts([]);
        setWithdrawals([]);
        return;
      }
      
      const mentorId = mentorData.id;
      // Detectar formato da taxa de comissão e padronizar
      const rawCommissionRate = mentorData.commission_rate || 0.2; // Default 20%
      
      // Se for um valor menor que 1, assumir que já está em decimal (0.2 = 20%)
      // Se for maior que 1, assumir que está em porcentagem (20 = 20%)
      const commissionRateAsPercentage = rawCommissionRate < 1 
        ? rawCommissionRate * 100  // 0.2 → 20
        : rawCommissionRate;       // 20 → 20
      
      const commissionRateAsDecimal = rawCommissionRate < 1 
        ? rawCommissionRate        // 0.2 → 0.2
        : rawCommissionRate / 100; // 20 → 0.2
      
      console.log(`💰 [useMentorFinance] Mentor ${mentorId} com comissão de ${commissionRateAsPercentage}% (${commissionRateAsDecimal} decimal)`);
      
      // Helper function to calculate net amount after commission
      const calculateNetAmount = (grossAmountCents: number) => {
        return Math.round(grossAmountCents * (1 - commissionRateAsDecimal));
      };
      
      // Fetch bank accounts (usando mentor_profile_id que é o profile_id do mentor)
      const { data: bankData, error: bankError } = await supabase
        .from('mentor_bank_accounts')
        .select('*')
        .eq('mentor_profile_id', user.id)
        .eq('is_active', true);
      
      if (bankError) {
        console.error('❌ [useMentorFinance] Error fetching bank accounts:', bankError);
      } else {
        console.log(`💳 [useMentorFinance] Bank accounts found: ${bankData?.length || 0}`);
        setBankAccounts(bankData || []);
      }
      
      // Fetch withdrawals (usando mentor_profile_id que é o profile_id do mentor)
      const { data: withdrawalData, error: withdrawalError } = await supabase
        .from('mentor_withdrawals')
        .select(`
          *,
          bank_account:mentor_bank_accounts(*)
        `)
        .eq('mentor_profile_id', user.id)
        .order('requested_at', { ascending: false });
      
      if (withdrawalError) {
        console.error('❌ [useMentorFinance] Error fetching withdrawals:', withdrawalError);
      } else {
        console.log(`💸 [useMentorFinance] Withdrawals found: ${withdrawalData?.length || 0}`);
        setWithdrawals(withdrawalData || []);
      }
      
      // Fetch digital content earnings
      const { data: digitalContentData, error: digitalContentError } = await supabase
        .rpc('get_mentor_digital_content_earnings', {
          p_mentor_id: mentorId
        });

      let digitalContentEarnings = 0;
      let digitalContentPendingRelease = 0;
      let digitalContentAvailable = 0;

      if (!digitalContentError && digitalContentData && digitalContentData.length > 0) {
        const dcStats = digitalContentData[0];
        digitalContentEarnings = dcStats.total_net_earnings_cents || 0;
        digitalContentPendingRelease = dcStats.pending_release_cents || 0;
        digitalContentAvailable = dcStats.available_for_withdrawal_cents || 0;
        
        console.log(`📱 [useMentorFinance] Conteúdos digitais:`, {
          vendas: dcStats.total_sales_count,
          ganhosBrutos: dcStats.total_gross_earnings_cents,
          ganhosLiquidos: digitalContentEarnings,
          disponivelSaque: digitalContentAvailable,
          aguardandoLiberacao: digitalContentPendingRelease
        });
      }
      
      // Fetch all mentor payments for detailed calculations (mentorias)
      // Buscar sessões e pagamentos separadamente para evitar problemas de RLS
      console.log(`🔍 [useMentorFinance] Buscando sessões para mentor_id: ${mentorId}`);
      
      // 1. Buscar sessões
      const { data: allSessions, error: sessionsError } = await supabase
        .from('sessions')
        .select(`
          id,
          status,
          payment_released
        `)
        .eq('mentor_id', mentorId);
      
      console.log(`📊 [useMentorFinance] Sessões encontradas:`, {
        count: allSessions?.length || 0,
        error: sessionsError
      });
      
      if (sessionsError) {
        console.error(`❌ [useMentorFinance] Erro ao buscar sessões:`, sessionsError);
      }
      
      // 2. Buscar pagamentos separadamente
      // Tentar duas abordagens: por session_id e por mentor_id (profile_id)
      let allPayments: any[] = [];
      if (!sessionsError && allSessions && allSessions.length > 0) {
        const sessionIds = allSessions.map(s => s.id);
        console.log(`💳 [useMentorFinance] Buscando pagamentos para ${sessionIds.length} sessões...`);
        
        // Abordagem 1: Buscar por session_id
        const { data: paymentsBySession, error: paymentsError1 } = await supabase
          .from('payments')
          .select(`
            id,
            session_id,
            amount_cents,
            status,
            released_by_student,
            available_for_withdrawal,
            mentor_id
          `)
          .in('session_id', sessionIds);
        
        if (paymentsError1) {
          console.error(`❌ [useMentorFinance] Erro ao buscar pagamentos por session_id:`, paymentsError1);
        } else {
          allPayments = paymentsBySession || [];
          console.log(`💳 [useMentorFinance] Pagamentos encontrados por session_id: ${allPayments.length}`);
        }
        
        // Abordagem 2: Se não encontrou nada, tentar buscar por mentor_id (profile_id)
        // Isso pode ser necessário se os pagamentos foram salvos com mentor_id incorreto
        if (allPayments.length === 0) {
          console.log(`💳 [useMentorFinance] Tentando buscar pagamentos por mentor_id (profile_id)...`);
          const { data: paymentsByMentor, error: paymentsError2 } = await supabase
            .from('payments')
            .select(`
              id,
              session_id,
              amount_cents,
              status,
              released_by_student,
              available_for_withdrawal,
              mentor_id
            `)
            .eq('mentor_id', user.id); // user.id é o profile_id
          
          if (paymentsError2) {
            console.error(`❌ [useMentorFinance] Erro ao buscar pagamentos por mentor_id:`, paymentsError2);
          } else {
            // Filtrar apenas pagamentos cujo session_id está na lista de sessões do mentor
            const filteredPayments = (paymentsByMentor || []).filter(p => sessionIds.includes(p.session_id));
            allPayments = filteredPayments;
            console.log(`💳 [useMentorFinance] Pagamentos encontrados por mentor_id: ${filteredPayments.length} (de ${paymentsByMentor?.length || 0} total)`);
          }
        }
        
        // Log detalhado dos pagamentos encontrados
        if (allPayments.length > 0) {
          console.log(`💳 [useMentorFinance] Detalhes dos pagamentos encontrados:`, allPayments.map(p => ({
            id: p.id,
            session_id: p.session_id,
            amount: p.amount_cents,
            status: p.status,
            mentor_id: p.mentor_id
          })));
        }
      }
      
      // 3. Processar sessões e fazer match com pagamentos
      if (!sessionsError) {
        let totalEarnedGross = 0;
        let pendingReleasesGross = 0;
        let availableForWithdrawalGross = 0;
        
        const sessionsToProcess = allSessions || [];
        console.log(`🔍 [useMentorFinance] Analisando ${sessionsToProcess.length} sessões do mentor`);
        
        sessionsToProcess.forEach((session, index) => {
          // Buscar pagamentos para esta sessão
          const sessionPayments = allPayments.filter(p => p.session_id === session.id);
          
          if (sessionPayments.length > 0) {
            // Buscar o pagamento completed (pode haver múltiplos)
            const completedPayment = sessionPayments.find(p => p.status === 'completed');
            
            if (completedPayment) {
              console.log(`  ${index + 1}. Sessão ${session.id}:`, {
                paymentStatus: completedPayment.status,
                amount: completedPayment.amount_cents,
                releasedByStudent: completedPayment.released_by_student,
                availableForWithdrawal: completedPayment.available_for_withdrawal
              });
              
              totalEarnedGross += completedPayment.amount_cents;
              
              // Count payments awaiting student release
              if (!completedPayment.released_by_student) {
                pendingReleasesGross += completedPayment.amount_cents;
              } else {
                // Available for withdrawal (released by student)
                availableForWithdrawalGross += completedPayment.amount_cents;
              }
            } else {
              console.log(`  ${index + 1}. Sessão ${session.id}: TEM PAGAMENTOS MAS NENHUM COMPLETED`, {
                payments: sessionPayments.map(p => ({ status: p.status, amount: p.amount_cents }))
              });
            }
          } else {
            console.log(`  ${index + 1}. Sessão ${session.id}: SEM PAGAMENTOS (status: ${session.status})`);
          }
        });
        
        console.log(`💰 [useMentorFinance] Valores brutos das mentorias:`, {
          totalBruto: `R$ ${(totalEarnedGross / 100).toFixed(2)}`,
          pendingReleaseBruto: `R$ ${(pendingReleasesGross / 100).toFixed(2)}`,
          availableForWithdrawalBruto: `R$ ${(availableForWithdrawalGross / 100).toFixed(2)}`
        });
        
        // Apply commission deduction to mentorship values
        const mentorshipEarnings = calculateNetAmount(totalEarnedGross);
        const mentorshipPendingReleases = calculateNetAmount(pendingReleasesGross);
        let mentorshipAvailableForWithdrawal = calculateNetAmount(availableForWithdrawalGross);
        
        console.log(`💰 [useMentorFinance] Valores líquidos das mentorias (após ${commissionRateAsPercentage}% comissão):`, {
          totalLiquido: `R$ ${(mentorshipEarnings / 100).toFixed(2)}`,
          pendingReleaseLiquido: `R$ ${(mentorshipPendingReleases / 100).toFixed(2)}`,
          availableForWithdrawalLiquido: `R$ ${(mentorshipAvailableForWithdrawal / 100).toFixed(2)}`
        });
        
        // Calculate total values (mentorias + conteúdos digitais)
        const totalEarned = mentorshipEarnings + digitalContentEarnings;
        const pendingReleases = mentorshipPendingReleases + digitalContentPendingRelease;
        let totalAvailableForWithdrawal = mentorshipAvailableForWithdrawal + digitalContentAvailable;
        
        // Calculate pending withdrawals and total withdrawn
        const pendingWithdrawals = withdrawalData
          ? withdrawalData
              .filter(w => w.status === 'pending')
              .reduce((sum, w) => sum + w.amount_cents, 0)
          : 0;
        
        // Calculate total withdrawn (only approved withdrawals)
        const totalWithdrawn = withdrawalData
          ? withdrawalData
              .filter(w => w.status === 'approved')
              .reduce((sum, w) => sum + w.amount_cents, 0)
          : 0;
        
        // Adjust available for withdrawal by subtracting pending and completed withdrawals
        totalAvailableForWithdrawal = Math.max(0, totalAvailableForWithdrawal - totalWithdrawn - pendingWithdrawals);
        
        console.log(`📊 [useMentorFinance] Resumo financeiro calculado (com comissão ${commissionRateAsPercentage}%):`);
        console.log(`💰 Total ganho (líquido): R$ ${(totalEarned / 100).toFixed(2)}`);
        console.log(`  - Mentorias: R$ ${(mentorshipEarnings / 100).toFixed(2)}`);
        console.log(`  - Conteúdos Digitais: R$ ${(digitalContentEarnings / 100).toFixed(2)}`);
        console.log(`✅ Disponível para saque: R$ ${(totalAvailableForWithdrawal / 100).toFixed(2)}`);
        console.log(`⏳ Aguardando liberação: R$ ${(pendingReleases / 100).toFixed(2)}`);
        console.log(`  - Mentorias (aluno): R$ ${(mentorshipPendingReleases / 100).toFixed(2)}`);
        console.log(`  - Conteúdos (7 dias): R$ ${(digitalContentPendingRelease / 100).toFixed(2)}`);
        console.log(`🔄 Saques pendentes: R$ ${(pendingWithdrawals / 100).toFixed(2)}`);
        console.log(`💸 Total sacado: R$ ${(totalWithdrawn / 100).toFixed(2)}`);
        
        setFinanceSummary({
          totalEarned,
          availableForWithdrawal: totalAvailableForWithdrawal,
          pendingWithdrawals,
          pendingReleases,
          totalWithdrawn,
          digitalContentEarnings,
          digitalContentPendingRelease,
          digitalContentAvailable,
        });
      } else {
        // Erro ao buscar sessões - usar apenas conteúdos digitais
        console.warn(`⚠️ [useMentorFinance] Erro ao buscar sessões, usando apenas conteúdos digitais`);
        const pendingWithdrawals = withdrawalData
          ? withdrawalData
              .filter(w => w.status === 'pending')
              .reduce((sum, w) => sum + w.amount_cents, 0)
          : 0;
        
        const totalWithdrawn = withdrawalData
          ? withdrawalData
              .filter(w => w.status === 'approved')
              .reduce((sum, w) => sum + w.amount_cents, 0)
          : 0;
        
        const availableForWithdrawal = Math.max(0, digitalContentAvailable - totalWithdrawn - pendingWithdrawals);
        
        setFinanceSummary({
          totalEarned: digitalContentEarnings,
          availableForWithdrawal,
          pendingWithdrawals,
          pendingReleases: digitalContentPendingRelease,
          totalWithdrawn,
          digitalContentEarnings,
          digitalContentPendingRelease,
          digitalContentAvailable,
        });
      }
      
    } catch (error) {
      console.error('Error fetching finance data:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar dados financeiros',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const requestWithdrawal = async (bankAccountId: string, amountCents: number) => {
    if (!user) return false;
    
    try {
      setIsProcessing(true);
      
      console.log(`💸 [requestWithdrawal] Solicitando saque de R$ ${(amountCents / 100).toFixed(2)}`);
      
      // Obter o token de autenticação
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Token de autenticação não encontrado');
      }
      
      // Chamar a Edge Function que cria o saque E envia o email ao mentor
      const { data: result, error } = await supabase.functions.invoke('request-withdrawal', {
        body: { 
          bankAccountId, 
          amountCents 
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (error) {
        console.error(`❌ [requestWithdrawal] Erro na Edge Function:`, error);
        throw new Error(`Erro ao solicitar saque: ${error.message}`);
      }
      
      console.log(`📊 [requestWithdrawal] Resultado da Edge Function:`, result);
      
      if (!result?.success) {
        console.error(`❌ [requestWithdrawal] Edge Function retornou erro:`, result?.error);
        throw new Error(result?.error || 'Erro desconhecido ao solicitar saque');
      }
      
      console.log(`✅ [requestWithdrawal] Saque solicitado com sucesso!`);
      console.log(`📧 [requestWithdrawal] Email de confirmação enviado ao mentor`);
      
      toast({
        title: 'Sucesso',
        description: 'Solicitação de saque enviada com sucesso. Você receberá um email de confirmação.',
      });
      
      // Refresh data
      await fetchFinanceData();
      
      return true;
    } catch (error: any) {
      console.error('Error requesting withdrawal:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao solicitar saque',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const addBankAccount = async (bankData: Omit<BankAccount, 'id' | 'is_active'>) => {
    if (!user) return false;
    
    try {
      setIsProcessing(true);
      
      // Get mentor ID
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor_info')
        .select('id')
        .eq('profile_id', user.id)
        .single();
      
      if (mentorError || !mentorData) {
        throw new Error('Mentor não encontrado');
      }
      
      const { error } = await supabase
        .from('mentor_bank_accounts')
        .insert({
          mentor_profile_id: user.id, // Usar profile_id em vez de mentor_id
          ...bankData,
          is_active: true,
        });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Sucesso',
        description: 'Conta bancária adicionada com sucesso',
      });
      
      // Refresh data
      await fetchFinanceData();
      
      return true;
    } catch (error: any) {
      console.error('Error adding bank account:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao adicionar conta bancária',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    loading,
    bankAccounts,
    withdrawals,
    financeSummary,
    isProcessing,
    requestWithdrawal,
    addBankAccount,
    refreshData: fetchFinanceData,
  };
};
